import { assert } from "@lib/utils/utils";
import Vector, { normalizeIndex } from "./vector";

declare const DimensionSymbol: unique symbol;

export interface Dimension<Name extends string> {
  readonly [DimensionSymbol]: Name;
}

// Matrix class with proper type tracking
export default class Matrix<R extends string, C extends string> {
  data: Float32Array;
  n_rows: number;
  n_cols: number;
  [n: number]: never;

  // Use private properties to enforce nominal typing
  private readonly _rowDim!: Dimension<R>;
  private readonly _colDim!: Dimension<C>;

  constructor(data: number[][]) {
    this.data = new Float32Array(data.flat());
    this.n_rows = data.length;
    this.n_cols = data.length > 0 ? data[0].length : 0;
  }

  get shape() {
    return [this.n_rows, this.n_cols];
  }

  get size() {
    return this.n_rows * this.n_cols;
  }

  static zeros<R extends string, C extends string>(
    rows: number,
    cols: number,
  ): Matrix<R, C> {
    // Create arrays with proper dimensions
    const data = Array(rows)
      .fill(0)
      .map(() => Array(cols).fill(0));
    return new Matrix<R, C>(data);
  }

  static ones<R extends string, C extends string>(
    rows: number,
    cols: number,
  ): Matrix<R, C> {
    const data = Array(rows)
      .fill(0)
      .map(() => Array(cols).fill(1));
    return new Matrix<R, C>(data);
  }

  static ofRows<R extends string, C extends string>(
    rows: Vector<C>[],
  ): Matrix<R, C> {
    return new Matrix<R, C>(rows.map((row) => [...row.data]));
  }

  // Only allow explicit dimension renaming
  rename<NewR extends string, NewC extends string>(
    _rowRename: `${R} -> ${NewR}`,
    _colRename: `${C} -> ${NewC}`,
  ): Matrix<NewR, NewC> {
    // This cast is safe because we're explicitly changing the type
    return this as unknown as Matrix<NewR, NewC>;
  }

  zerosLike(): Matrix<R, C> {
    return Matrix.zeros<R, C>(this.n_rows, this.n_cols);
  }

  sum<Axis extends 0 | 1>(axis: Axis): Vector<Axis extends 0 ? C : R> {
    if (axis === 0) {
      // Sum columns to produce a row vector with column dimension
      return new Vector<C>(
        Array(this.n_cols)
          .fill(0)
          .map((_, col) => this.getCol(col).sum()),
      ) as Vector<Axis extends 0 ? C : R>;
    } else {
      // Sum rows to produce a column vector with row dimension
      return new Vector<R>(
        Array(this.n_rows)
          .fill(0)
          .map((_, row) => this.getRow(row).sum()),
      ) as Vector<Axis extends 0 ? C : R>;
    }
  }

  get(row: number, col: number): number {
    const normalizedRow = normalizeIndex(row, this.n_rows);
    const normalizedCol = normalizeIndex(col, this.n_cols);
    return this.data[normalizedRow * this.n_cols + normalizedCol];
  }

  set(row: number, col: number, value: number): void {
    const normalizedRow = normalizeIndex(row, this.n_rows);
    const normalizedCol = normalizeIndex(col, this.n_cols);
    this.data[normalizedRow * this.n_cols + normalizedCol] = value;
  }

  getRow(row: number): Vector<C> {
    const normalizedRow = normalizeIndex(row, this.n_rows);
    return new Vector<C>(
      this.data.slice(
        normalizedRow * this.n_cols,
        (normalizedRow + 1) * this.n_cols,
      ),
    );
  }

  getCol(col: number): Vector<R> {
    const normalizedCol = normalizeIndex(col, this.n_cols);
    const result = new Float32Array(this.n_rows);
    for (let i = 0; i < this.n_rows; i++) {
      result[i] = this.get(i, normalizedCol);
    }
    return new Vector<R>(result);
  }

  get transpose(): Matrix<C, R> {
    return new Matrix<C, R>(
      Array.from({ length: this.n_cols }, (_, col) =>
        Array.from({ length: this.n_rows }, (_, row) => this.get(row, col)),
      ),
    );
  }

  toArrays(): number[][] {
    return Array.from({ length: this.n_rows }, (_, row) =>
      Array.from({ length: this.n_cols }, (_, col) => this.get(row, col)),
    );
  }

  // Type-safe element-wise multiplication
  mulColumns<V extends C>(operand: Vector<V> | number): Matrix<R, C> {
    if (operand instanceof Vector) {
      assert(
        operand.length === this.n_cols,
        "Vector length must match matrix number of columns",
      );
      return new Matrix<R, C>(
        this.toArrays().map((row) =>
          row.map((value, index) => value * operand.get(index)),
        ),
      );
    } else {
      return new Matrix<R, C>(
        this.toArrays().map((row) => row.map((value) => value * operand)),
      );
    }
  }
}
